import _ from 'lodash';
import React, { useState, useRef, useCallback } from 'react';
import useEvent from 'react-use-v13/lib/useEvent';
import { Line } from './namedEntityRecognitionParser';
import {
  LineRange,
  useLineSelectionRange,
  getAnnotationRangeFromSelectionRange,
} from './namedEntityRecognitionSelection';
import {
  INamedEntityRecognitionAnnotation,
  INamedEntityRecognitionRelationship,
} from './namedEntityRecognitionAnnotations';
import NERTextLine from 'react-scale/components/scalers/tasks/namedEntityRecognition/NERTextLine';
import { findLabel, IColorMap, INamedEntityRecognitionLabel } from './namedEntityRecognitionLabels';
import NERAnnotation, {
  NERAnnotationFocusLevel,
} from 'react-scale/components/scalers/tasks/namedEntityRecognition/NERAnnotation';
import NERAnnotationPreview from 'react-scale/components/scalers/tasks/namedEntityRecognition/NERAnnotationPreview';
import Fuse from 'fuse.js';
import NERSelection from './NERSelection';
import NERAnnotationClumpLabel from './NERAnnotationClumpLabel';
import NERLinkingModeHeader from './NERLinkingModeHeader';
import {
  INamedEntityRecognitionAttribute,
  INamedEntityRecognitionRelationshipDefinition,
} from '@scale/shared/types/params/namedentityrecognition';
import { INamedEntityRecognitionResponseAttribute } from '@scale/shared/types/response/namedentityrecognition';
import NERLink, { NERLinkDirection } from './NERLink';
import { augmentResponseRelationship } from './namedEntityRecognitionLinking';
import NERWatermark from './NERWatermark';
import { useLineRangePositions } from './useLineRangePositions';
import { LabelAttributes } from '@scale/shared/namedentityrecognition/label';

const PREVIEW_LINK_ID = 'PREVIEW_LINK_NOT_A_REAL_LINK';

interface INERTextCanvasProps {
  textDirection?: 'rtl' | 'ltr';
  labels: INamedEntityRecognitionLabel[];
  labelColormap: IColorMap;
  lines: Line[];
  visibleTokenRange?: [number, number];
  sortedAnnotations: INamedEntityRecognitionAnnotation[];
  preserveTrailingPunctuation?: boolean;
  allowOverlappingAnnotations?: boolean;
  defaultFreeSelect?: boolean;
  focusedAnnotationIds: Set<string>;
  relationshipDefinitions?: INamedEntityRecognitionRelationshipDefinition[];
  relationships?: INamedEntityRecognitionRelationship[];
  defaultAttributeFields?: Record<string, INamedEntityRecognitionAttribute>;
  focusAnnotationById: (annotationId: string) => void;
  unFocusAnnotation: () => void;
  addAnnotationFromRange: (range: LineRange, label: INamedEntityRecognitionLabel) => void;
  updateAnnotationLabelById: (annotationId: string, label: string) => void;
  updateAnnotationAttributeById: (
    annotationId: string,
    key: string,
    value: INamedEntityRecognitionResponseAttribute,
  ) => void;
  setRelationship?: (
    relationship: INamedEntityRecognitionRelationship,
    relationshipId?: string,
  ) => void;
  removeRelationship?: (relationshipId: string) => void;
  getValidRelationshipsForAnnotationPair?: (
    sourceAnnotation: INamedEntityRecognitionAnnotation,
    targetAnnotation: INamedEntityRecognitionAnnotation,
  ) => INamedEntityRecognitionRelationship[];
  getValidRelationshipTargets?: (
    sourceAnnotation: INamedEntityRecognitionAnnotation,
    annotations: INamedEntityRecognitionAnnotation[],
    relationships: INamedEntityRecognitionRelationship[],
  ) => string[];
  contentClassName?: string;
  textClassName: string;
  labelSearchIndex: Fuse<INamedEntityRecognitionLabel>;
  useProtectedText?: boolean;
  loggedUserId?: string;
  labelAttributeTable: Map<string, LabelAttributes>;
  hideNERAnnotationPreview?: boolean;
  removeFocusedAnnotation?: () => void;
  checkRequiredAttributeExist?: (annotationId: string, key: string) => boolean;
  isResponsePreview?: boolean;
  readOnly?: boolean; // Added readOnly prop
}

export const NERTextCanvas = (props: INERTextCanvasProps) => {
  const {
    labels,
    labelColormap,
    lines,
    sortedAnnotations,
    // preserveTrailingPunctuation,
    allowOverlappingAnnotations,
    defaultFreeSelect,
    focusedAnnotationIds,
    relationshipDefinitions = [],
    relationships = [],
    defaultAttributeFields,
    focusAnnotationById,
    unFocusAnnotation,
    addAnnotationFromRange,
    updateAnnotationLabelById,
    updateAnnotationAttributeById,
    setRelationship = _.noop,
    removeRelationship = _.noop,
    getValidRelationshipsForAnnotationPair = () => [],
    getValidRelationshipTargets = () => [],
    contentClassName,
    textClassName,
    labelSearchIndex,
    useProtectedText,
    loggedUserId,
    labelAttributeTable,
    hideNERAnnotationPreview,
    removeFocusedAnnotation = _.noop,
    checkRequiredAttributeExist = (_annotationId, _key) => true,
    isResponsePreview,
    textDirection,
    readOnly, // Destructured readOnly prop
  } = props;

  const lineSelectionRange = useLineSelectionRange(sortedAnnotations, allowOverlappingAnnotations);

  const [annotationPreviewRange, setAnnotationPreviewRange] = useState<LineRange | null>(null);
  const [relationshipPreview, setRelationshipPreview] =
    useState<INamedEntityRecognitionRelationship | null>(null);

  const [hoverredAnnotationId, setHoverredAnnotationId] = useState<string | null>(null);
  const [linkingModeBaseAnnotationId, setLinkingModeBaseAnnotationId] = useState<string | null>(
    null,
  );
  const [hiddenRelationships, setHiddenRelationships] = useState<{ [id: string]: boolean }>({});

  const toggleRelationshipVisibility = useCallback(
    (id: string) => {
      setHiddenRelationships({
        ...hiddenRelationships,
        [id]: !hiddenRelationships[id],
      });
    },
    [hiddenRelationships, setHiddenRelationships],
  );

  const onFocusAnnotation = (annotationId: string) => {
    if (linkingModeBaseAnnotationId) {
      const sourceAnnotation = sortedAnnotations.find(
        ({ id }) => id === linkingModeBaseAnnotationId,
      );
      const targetAnnotation = sortedAnnotations.find(({ id }) => id === annotationId);
      if (!sourceAnnotation || !targetAnnotation || !getValidRelationshipsForAnnotationPair) {
        return;
      }

      const validRelationships = getValidRelationshipsForAnnotationPair(
        sourceAnnotation,
        targetAnnotation,
      );
      if (!validRelationships.length) {
        return;
      } else if (validRelationships.length === 1) {
        setRelationship(validRelationships[0]);
      } else {
        const newRelationshipPreview = augmentResponseRelationship(
          validRelationships[0],
          [sourceAnnotation, targetAnnotation],
          relationshipDefinitions.find(({ name }) => name === validRelationships[0].name)!,
        );
        if (!newRelationshipPreview) {
          return;
        }
        newRelationshipPreview.name = undefined;
        newRelationshipPreview.id = PREVIEW_LINK_ID;
        setRelationshipPreview(newRelationshipPreview);
      }
    } else {
      focusAnnotationById(annotationId);
    }
  };

  const onSetRelationship = (
    relationship: INamedEntityRecognitionRelationship,
    relationshipId?: string,
  ) => {
    setRelationship(relationship, relationshipId);
    setRelationshipPreview(null);
  };

  const onEnterLinkingMode = (annotationId: string) => {
    unFocusAnnotation();
    setLinkingModeBaseAnnotationId(annotationId);
  };

  const validLinkingTargets = React.useMemo(() => {
    const linkingModeBaseAnnotation = sortedAnnotations.find(
      ({ id }) => id === linkingModeBaseAnnotationId,
    );
    if (!linkingModeBaseAnnotation) {
      return null;
    }
    return getValidRelationshipTargets(linkingModeBaseAnnotation, sortedAnnotations, relationships);
  }, [linkingModeBaseAnnotationId, sortedAnnotations, relationships, getValidRelationshipTargets]);

  const linesContainer = useRef<HTMLDivElement>(null);
  const [linesContainerReady, setLinesContainerReady] = useState(false);
  const annotationAnchorOffsets = useRef<{ [annotationId: string]: number }>({});

  React.useEffect(() => {
    if (linesContainer.current !== null) {
      setLinesContainerReady(true);
    }
  }, [linesContainer]);

  const setAnnotationAnchorOffset = React.useCallback(
    (annotationId: string, anchorOffset: number) => {
      annotationAnchorOffsets.current[annotationId] = anchorOffset;
    },
    [],
  );

  const handleNewAnnotation = (label: INamedEntityRecognitionLabel) => {
    if (!annotationPreviewRange) return;
    addAnnotationFromRange(annotationPreviewRange, label);
    setAnnotationPreviewRange(null);
  };

  const handleNewAnnotationPreview = (
    lineSelectionRange: LineRange | null,
    allowSubstringSelection: boolean,
  ) => {
    if (!lineSelectionRange) return;

    const annotationRange = getAnnotationRangeFromSelectionRange(
      lineSelectionRange,
      lines,
      sortedAnnotations,
      allowSubstringSelection,
      allowOverlappingAnnotations,
      // preserveTrailingPunctuation,
    );

    // TODO: account for preserveTrailingPunctuation
    setAnnotationPreviewRange(annotationRange);
  };

  useEvent('mouseup', (event: MouseEvent) => {
    if (isResponsePreview) {
      return;
    }

    if (linesContainer.current && linesContainer.current.contains(event.target as Node)) {
      handleNewAnnotationPreview(
        lineSelectionRange,
        (defaultFreeSelect && !event.altKey) || (!defaultFreeSelect && event.altKey),
      );
    }
  });

  const linesContainerBox = linesContainer.current?.getBoundingClientRect();
  const { setLineElement, lineElementsReady, getLineRangeBoxes } = useLineRangePositions(
    lines,
    linesContainerBox,
  );

  // Rendering overlapping annotations is nontrivial. We need to compute a "depth" (height) at
  // which to render each annotation, such that no overlapping annotations have the same depth.
  // We also need to compute "clumps", defined as a maximal chain of overlapping annotations such
  // that at least one annotation in the chain has depth > 2.
  const { annotationDepths, lineMaxDepths, isAnnotationClumped, annotationClumps } =
    React.useMemo(() => {
      const endSortedAnnotations = _.sortBy(sortedAnnotations, [
        ({ endLineIdx }) => endLineIdx,
        ({ endOffset }) => endOffset,
      ]);

      const currentUsedDepths = new Set<number>();
      const annotationDepths: { [id: string]: number } = {};

      let currentLineMaxDepth = 0;
      const lineMaxDepths: number[] = [];

      let currentClumpMaxDepth = 0;
      let currentClumpedAnnotations: INamedEntityRecognitionAnnotation[] = [];
      const isAnnotationClumped: { [id: string]: boolean } = {};
      const annotationClumps: INamedEntityRecognitionAnnotation[][] = [];

      let annotationStartIdx = 0;
      let annotationEndIdx = 0;
      let lineIdx = 0;
      while (lineIdx < lines.length) {
        const nextStartingAnnotation = sortedAnnotations[annotationStartIdx];
        const nextEndingAnnotation = endSortedAnnotations[annotationEndIdx];
        if (
          nextStartingAnnotation?.startLineIdx === lineIdx &&
          (nextEndingAnnotation?.endLineIdx !== lineIdx ||
            nextStartingAnnotation.startOffset < nextEndingAnnotation.endOffset)
        ) {
          // Process a starting annotation.
          annotationStartIdx++;
          let depth = 0;
          for (depth; currentUsedDepths.has(depth); depth++);
          currentUsedDepths.add(depth);
          annotationDepths[nextStartingAnnotation.id] = depth;
          currentLineMaxDepth = Math.max(currentLineMaxDepth, depth);
          currentClumpMaxDepth = Math.max(currentClumpMaxDepth, depth);
          currentClumpedAnnotations.push(nextStartingAnnotation);
        } else if (nextEndingAnnotation?.endLineIdx === lineIdx) {
          // Process an ending annotation. In particular, we need to check whether the current
          // clump is ending.
          annotationEndIdx++;
          currentUsedDepths.delete(annotationDepths[nextEndingAnnotation.id]);
          if (_.size(currentUsedDepths) === 0) {
            if (currentClumpMaxDepth > 1) {
              annotationClumps.push(currentClumpedAnnotations);
              for (const annotation of currentClumpedAnnotations) {
                isAnnotationClumped[annotation.id] = true;
              }
            } else {
              for (const annotation of currentClumpedAnnotations) {
                isAnnotationClumped[annotation.id] = false;
              }
            }
            currentClumpMaxDepth = 0;
            currentClumpedAnnotations = [];
          }
        } else {
          // Process an EOL.
          lineIdx++;
          lineMaxDepths.push(currentLineMaxDepth);
          currentLineMaxDepth =
            _.max(currentClumpedAnnotations.map(({ id }) => annotationDepths[id])) ?? 0;
        }
      }

      return {
        annotationDepths,
        lineMaxDepths,
        isAnnotationClumped,
        annotationClumps,
      };
    }, [sortedAnnotations, lines]);

  // Similarly, we need to compute link depths.
  const { linkDepths, lineMaxDepths: lineMaxLinkDepths } = React.useMemo(() => {
    const relationshipsWithPreview = relationshipPreview
      ? _.concat(relationships, relationshipPreview)
      : relationships;

    const sortedRelationships = _.sortBy(relationshipsWithPreview, [
      relationship => relationship.topAnnotation.startLineIdx,
      relationship => relationship.topAnnotation.startOffset,
      relationship => relationship.bottomAnnotation.startLineIdx,
      relationship => relationship.bottomAnnotation.startOffset,
    ]);
    const endSortedRelationships = _.sortBy(relationshipsWithPreview, [
      relationship => relationship.bottomAnnotation.startLineIdx,
      relationship => relationship.bottomAnnotation.startOffset,
      relationship => relationship.topAnnotation.startLineIdx,
      relationship => relationship.topAnnotation.startOffset,
    ]);

    const currentUsedDepths = new Set<number>();
    const linkDepths: { [id: string]: [number, number] } = {};

    let currentLineMaxDepth = 0;
    const lineMaxDepths: number[] = [];

    let linkStartIdx = 0;
    let linkEndIdx = 0;
    let lineIdx = 0;
    while (lineIdx < lines.length) {
      const nextStartingLink = sortedRelationships[linkStartIdx];
      const nextEndingLink = endSortedRelationships[linkEndIdx];
      if (
        nextStartingLink?.topAnnotation.startLineIdx === lineIdx &&
        (nextEndingLink?.bottomAnnotation.startLineIdx !== lineIdx ||
          nextStartingLink?.topAnnotation.startOffset <=
            nextEndingLink?.bottomAnnotation.startOffset)
      ) {
        // Process a starting link.
        linkStartIdx++;
        let depth = 1;
        for (depth; currentUsedDepths.has(depth); depth++);
        currentUsedDepths.add(depth);
        linkDepths[nextStartingLink.id] = [depth, depth];
        currentLineMaxDepth = Math.max(currentLineMaxDepth, depth);
      } else if (nextEndingLink?.bottomAnnotation.startLineIdx === lineIdx) {
        // Process an ending link.
        linkEndIdx++;
        if (
          nextEndingLink.topAnnotation.startLineIdx === nextEndingLink.bottomAnnotation.startLineIdx
        ) {
          currentUsedDepths.delete(linkDepths[nextEndingLink.id][0]);
        } else {
          let depth = 1;
          for (depth; currentUsedDepths.has(depth); depth++);
          currentUsedDepths.add(depth);
          linkDepths[nextEndingLink.id][1] = depth;
          currentLineMaxDepth = Math.max(currentLineMaxDepth, depth);
        }
      } else {
        // Process an EOL.
        lineIdx++;
        lineMaxDepths.push(currentLineMaxDepth);
        currentUsedDepths.clear();
        currentLineMaxDepth = 0;
      }
    }

    return {
      linkDepths,
      lineMaxDepths,
    };
  }, [relationships, relationshipPreview, lines]);

  const getRelationshipsForAnnotation = React.useCallback(
    (annotationId: string) =>
      relationships.filter(
        relationship =>
          relationship.source_ref === annotationId || relationship.target_ref === annotationId,
      ),
    [relationships],
  );

  const renderAnnotations = () => {
    const someAnnotationHovered =
      hoverredAnnotationId != null || linkingModeBaseAnnotationId != null;

    const annotations = sortedAnnotations.map(annotation => {
      const label = findLabel(labels, annotation.labelId);

      if (!label) {
        return null;
      }

      let focusLevel = someAnnotationHovered
        ? NERAnnotationFocusLevel.Faded
        : NERAnnotationFocusLevel.Inactive;

      const isAnnotationFocused = focusedAnnotationIds.has(annotation.id);
      const isAnnotationHoverred = hoverredAnnotationId === annotation.id;
      const isAnnotationBeingLinked = linkingModeBaseAnnotationId === annotation.id;
      const isAnnotationDisabled =
        !!linkingModeBaseAnnotationId && !validLinkingTargets?.includes(annotation.id);

      if (isAnnotationFocused) {
        focusLevel = NERAnnotationFocusLevel.Selected;
      } else if (isAnnotationHoverred || isAnnotationBeingLinked) {
        focusLevel = NERAnnotationFocusLevel.Hoverred;
      } else if (isAnnotationDisabled) {
        focusLevel = NERAnnotationFocusLevel.Disabled;
      }

      const onMouseEnter = isResponsePreview
        ? undefined
        : isAnnotationDisabled
        ? undefined
        : () => setHoverredAnnotationId(annotation.id);
      const onMouseLeave = isResponsePreview
        ? undefined
        : isAnnotationDisabled
        ? undefined
        : () => setHoverredAnnotationId(id => (id === annotation.id ? null : id));

      return (
        <NERAnnotation
          key={`annotation-${annotation.id}`}
          ref={linesContainer}
          annotationId={annotation.id}
          clientBoxes={getLineRangeBoxes(annotation)}
          label={label}
          labels={labels}
          labelColormap={labelColormap}
          labelSearchIndex={labelSearchIndex}
          relationships={getRelationshipsForAnnotation(annotation.id)}
          defaultAttributeFields={defaultAttributeFields}
          hiddenRelationships={hiddenRelationships}
          toggleRelationshipVisibility={toggleRelationshipVisibility}
          relationshipDefinitions={relationshipDefinitions}
          labelAttributes={labelAttributeTable.get(label.name)}
          attributeValues={annotation.attributes}
          showPopover={!readOnly && isAnnotationFocused} // Only show popover if not readOnly
          focusLevel={focusLevel}
          blockSelection={!lineSelectionRange && !allowOverlappingAnnotations}
          displayDepth={annotationDepths[annotation.id]}
          isAnnotationClumped={isAnnotationClumped[annotation.id]}
          onClick={
            isResponsePreview
              ? undefined
              : isAnnotationDisabled
              ? undefined
              : () => onFocusAnnotation(annotation.id)
          }
          onChangeLabel={label => updateAnnotationLabelById(annotation.id, label.name)}
          onChangeAttributeValue={(key, value) =>
            updateAnnotationAttributeById(annotation.id, key, value)
          }
          onClickAway={unFocusAnnotation}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onEnterLinkingMode={() => onEnterLinkingMode(annotation.id)}
          onRemoveRelationship={removeRelationship}
          onSetAnnotationAnchorOffset={(offset: number) =>
            setAnnotationAnchorOffset(annotation.id, offset)
          }
          removeFocusedAnnotation={removeFocusedAnnotation}
          checkRequiredAttributeExist={key => checkRequiredAttributeExist(annotation.id, key)}
        />
      );
    });

    const annotationClumpLabels = annotationClumps.map(clump => {
      if (!clump.length) {
        return null;
      }
      const clumpLabels = clump.map(({ labelId }) => findLabel(labels, labelId)!);
      return (
        <NERAnnotationClumpLabel
          key={`annotation-clump-${clump[0]}`}
          ref={linesContainer}
          annotations={clump}
          labels={clumpLabels}
          labelColormap={labelColormap}
          clientBox={getLineRangeBoxes(clump[0])[0]}
          hoverAnnotationById={setHoverredAnnotationId}
          unHoverAnnotationById={oldId =>
            setHoverredAnnotationId(curId => (oldId === curId ? null : curId))
          }
          focusAnnotationById={onFocusAnnotation}
        />
      );
    });

    return _.concat(annotations, annotationClumpLabels);
  };

  const renderLinks = () =>
    relationships.map(relationship => {
      if (hiddenRelationships[relationship.id]) {
        return null;
      }
      const direction = !relationship.relationshipDefinition.is_directed
        ? NERLinkDirection.Undirected
        : relationship.sourceAnnotation.id === relationship.topAnnotation.id
        ? NERLinkDirection.TopToBottom
        : NERLinkDirection.BottomToTop;
      return (
        <NERLink
          key={relationship.id}
          ref={linesContainer}
          displayName={
            relationship.relationshipDefinition.display_name ??
            relationship.relationshipDefinition.name
          }
          direction={direction}
          currentRelationshipId={relationship.id}
          validRelationships={getValidRelationshipsForAnnotationPair(
            relationship.sourceAnnotation,
            relationship.targetAnnotation,
          )}
          onSetRelationship={onSetRelationship}
          isMultiLine={
            relationship.topAnnotation.startLineIdx !== relationship.bottomAnnotation.startLineIdx
          }
          toggleRelationshipVisibility={toggleRelationshipVisibility}
          topAnnotation={relationship.topAnnotation}
          topAnnotationClientBox={getLineRangeBoxes(relationship.topAnnotation)[0]}
          topAnnotationAnchorOffset={annotationAnchorOffsets.current[relationship.topAnnotation.id]}
          topAnnotationLinkDepth={linkDepths[relationship.id][0]}
          bottomAnnotation={relationship.bottomAnnotation}
          bottomAnnotationClientBox={getLineRangeBoxes(relationship.bottomAnnotation)[0]}
          bottomAnnotationAnchorOffset={
            annotationAnchorOffsets.current[relationship.bottomAnnotation.id]
          }
          bottomAnnotationLinkDepth={linkDepths[relationship.id][1]}
          labelColormap={labelColormap}
          containerWidth={linesContainer.current!.getBoundingClientRect().width}
        />
      );
    });

  return (
    <div className={contentClassName} ref={linesContainer} style={{ position: 'relative' }}>
      {linkingModeBaseAnnotationId && !relationshipPreview && (
        <NERLinkingModeHeader onExitLinkingMode={() => setLinkingModeBaseAnnotationId(null)} />
      )}
      {useProtectedText && <NERWatermark loggedUserId={loggedUserId} />}
      {lines.map((line, lineIdx) => (
        <NERTextLine
          key={`line-${lineIdx}`}
          ref={_.partial(setLineElement, lineIdx)}
          tokenIdx={lineIdx}
          text={line.value}
          textClassName={textClassName}
          maxDepth={lineMaxDepths[lineIdx]}
          maxLinkDepth={lineMaxLinkDepths[lineIdx]}
          disableSelection={!!linkingModeBaseAnnotationId}
          textDirection={textDirection}
        />
      ))}
      {linesContainerReady && lineElementsReady && (
        <>
          {!isResponsePreview && lineSelectionRange && (
            <NERSelection clientBoxes={getLineRangeBoxes(lineSelectionRange)} />
          )}
          {annotationPreviewRange && !hideNERAnnotationPreview && !readOnly && (
            <NERAnnotationPreview
              ref={linesContainer}
              clientBoxes={getLineRangeBoxes(annotationPreviewRange)}
              labels={labels}
              labelColormap={labelColormap}
              labelSearchIndex={labelSearchIndex}
              onLabelSelect={handleNewAnnotation}
              onClickAway={() => setAnnotationPreviewRange(null)}
            />
          )}
          {renderAnnotations()}
          {relationshipPreview && (
            <NERLink
              key={PREVIEW_LINK_ID}
              ref={linesContainer}
              isPreview
              onPreviewClose={() => setRelationshipPreview(null)}
              validRelationships={getValidRelationshipsForAnnotationPair(
                relationshipPreview.sourceAnnotation,
                relationshipPreview.targetAnnotation,
              )}
              onSetRelationship={onSetRelationship}
              toggleRelationshipVisibility={toggleRelationshipVisibility}
              isMultiLine={
                relationshipPreview.topAnnotation.startLineIdx !==
                relationshipPreview.bottomAnnotation.startLineIdx
              }
              topAnnotation={relationshipPreview.topAnnotation}
              topAnnotationClientBox={getLineRangeBoxes(relationshipPreview.topAnnotation)[0]}
              topAnnotationAnchorOffset={
                annotationAnchorOffsets.current[relationshipPreview.topAnnotation.id]
              }
              topAnnotationLinkDepth={linkDepths[relationshipPreview.id][0]}
              bottomAnnotation={relationshipPreview.bottomAnnotation}
              bottomAnnotationClientBox={getLineRangeBoxes(relationshipPreview.bottomAnnotation)[0]}
              bottomAnnotationAnchorOffset={
                annotationAnchorOffsets.current[relationshipPreview.bottomAnnotation.id]
              }
              bottomAnnotationLinkDepth={linkDepths[relationshipPreview.id][1]}
              labelColormap={labelColormap}
              containerWidth={linesContainer.current!.getBoundingClientRect().width}
            />
          )}
          {renderLinks()}{' '}
        </>
      )}
    </div>
  );
};
